import { Save } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, DialogActions, DialogContent, Step, type DialogProps } from '@mui/material';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { object, string } from 'yup';
import { useUploadDataSource } from '~/api/data-sources';
import { applyServerErrors } from '~/api/helpers';
import { Dialog, DialogTitle } from '~/components/dialogs/components';
import { StepLabel, Stepper } from '~/components/stepper';
import type { DataSourceKind } from '~/generated/graphql';
import { i18n } from '~/lib/i18n';
import { SelectKind, UploadForm } from './';

const STEPS = [
  i18n.t('pages.data_sources.new_dialog.step_choose'),
  i18n.t('pages.data_sources.new_dialog.step_specify'),
];

export interface NewDialogProps extends Omit<DialogProps, 'children'> {
  close: () => void;
  networkId: number;
}

export const NewDialog = ({ close, networkId, ...props }: NewDialogProps) => {
  const { t } = useTranslation();
  const [uploadDataSource] = useUploadDataSource();
  const [selectedKind, setSelectedKind] = useState<DataSourceKind>();
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      dataUri: '',
      foreignIdName: '',
      name: '',
    },
    onSubmit: async (values, { setErrors }) => {
      const result = await uploadDataSource({ variables: { input: { ...values, networkId } } });
      applyServerErrors(result, setErrors);
      if (result.data?.uploadDataSource?.dataSource) {
        navigate(`${result.data.uploadDataSource.dataSource.id}`);
      }
    },
    validateOnBlur: true,
    validateOnMount: true,
    validationSchema: object({
      dataUri: string().required(i18n.t('pages.data_sources.upload_form.validation.upload_label')),
      foreignIdName: string().label(
        i18n.t('pages.data_sources.upload_form.validation.unique_id_label'),
      ),
      name: string().required(i18n.t('pages.data_sources.upload_form.validation.name_label')),
    }),
  });

  const handleSelectKind = (kind: DataSourceKind) => {
    setSelectedKind(kind);
    setActiveStep(1);
  };

  const handleClose = async () => {
    close();
    setSelectedKind(undefined);
    setActiveStep(0);
    formik.resetForm();
    await formik.validateForm();
  };

  const getDialogTitle = () => {
    if (selectedKind) {
      return t('pages.data_sources.new_dialog.title_with_kind', { kind: selectedKind });
    }
    return t('pages.data_sources.new_dialog.title');
  };

  const getDialogMessage = () => {
    if (activeStep === 0) {
      return t('pages.data_sources.new_dialog.choose_message');
    }
    return t('pages.data_sources.new_dialog.specify_message', { kind: selectedKind });
  };

  return (
    <Dialog fullWidth maxWidth="md" onClose={handleClose} {...props}>
      <DialogTitle onClose={handleClose}>{getDialogTitle()}</DialogTitle>

      <DialogContent>
        <Box
          sx={{
            fontSize: '1.1rem',
            margin: (t) => t.spacing(2, 0),
            textAlign: 'center',
          }}
        >
          {getDialogMessage()}
        </Box>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ marginX: 15 }}>
          {STEPS.map((label, index) => (
            <Step key={label} completed={activeStep > index}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {activeStep === 0 && <SelectKind handleSelectKind={handleSelectKind} />}
        {activeStep === 1 && <UploadForm formik={formik} selectedKind={selectedKind} />}
      </DialogContent>

      {activeStep === 1 && (
        <DialogActions>
          <LoadingButton
            color="primary"
            disabled={!formik.isValid || formik.isSubmitting}
            form="new-data-source-form"
            loading={formik.isSubmitting}
            loadingPosition="start"
            startIcon={<Save />}
            type="submit"
            variant="contained"
          >
            {t('common.buttons.save')}
          </LoadingButton>
        </DialogActions>
      )}
    </Dialog>
  );
};
